"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { BsTrash3 } from "react-icons/bs";
import { IoChevronBack } from "react-icons/io5";
import { FaProjectDiagram } from "react-icons/fa";
import Panel from "./Panel";
import { motion } from "framer-motion";
import aboutIcon from "../../assets/icons/about.svg"
import projectsIcon from "../../assets/icons/projects.svg"
import certificatesIcon from "../../assets/icons/certficates.svg"
import photosIcon from "../../assets/icons/photos.svg"

export default function Window({ children, onClose, initialPosition, width = 837, height = 406,title, setTitle, panelType, setPanelType, imageClick,docClick,setSelectedProject,selectedProject,isSubPanelOpen, setIsSubPanelOpen}) {
  const [position, setPosition] = useState(initialPosition || { x: 0, y: 0 });
  const [isImagePanel, setIsImagePanel] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const windowRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const rect = windowRef.current?.getBoundingClientRect();
    if (rect) {
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragOffset.current.x,
      y: e.clientY - dragOffset.current.y,
    });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    setTitle(panelType);
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp,panelType,setTitle]);

  const handleSectionClick = (panelName) => {
    setIsSubPanelOpen(false);
    setPanelType(panelName);
    setTitle(panelName);
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60]"
        style={{ backgroundColor: "rgba(0,0,0,0.07)" }}
        onClick={onClose}
      />

      {/* Window - Framer style */}
      <motion.div
        ref={windowRef}
        className="fixed z-[70] overflow-hidden"
        initial={{ scale: 0.8, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        style={{
          width,
          height,
          left: position.x,
          top: position.y,
          borderRadius: 10,
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          cursor: isDragging ? "grabbing" : "default",
        }}
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full h-full flex">
          {/* Sidebar - Left panel */}
          <div
            className="w-[189px] h-full flex-shrink-0"
            style={{
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(236,236,236,0.85)",
              borderTopLeftRadius: 10,
              borderBottomLeftRadius: 10,
            }}
          >
            {/* Window Controls */}
            <div className="flex items-center gap-2 px-4 pt-3 pb-2">
              <button
                onClick={onClose}
                className="w-3 h-3 rounded-full flex items-center justify-center group"
                style={{
                  backgroundColor: "#ff6157",
                  border: "0.5px solid #e24640",
                }}
              >
                <svg
                  width="6"
                  height="6"
                  viewBox="0 0 6 6"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <path d="M0.5 0.5L5.5 5.5M5.5 0.5L0.5 5.5" stroke="#4c0002" strokeWidth="1" />
                </svg>
              </button>
              <button
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor: "#ffbd2e",
                  border: "0.5px solid #dea123",
                }}
              />
              <button
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor: "#28c940",
                  border: "0.5px solid #1bac2c",
                }}
              />
            </div>

            {/* Separator */}
            {/* <div className="w-full h-px bg-black/10 my-2" /> */}

            {/* Section 1 */}
            <div className="px-3 mt-2">
              <div className="text-[11px] font-semibold text-black/27 uppercase tracking-[-0.2px] mb-1 px-2">
                Section
              </div>

              {/* About Me */}
              <div
                className={
                  panelType === "about-me"
                    ? "flex items-center gap-2 px-2 py-1.5 rounded-[5px] bg-black/11 mb-0.5 text-[#0474F0]"
                    : "flex items-center gap-2 px-2 py-1.5 rounded-[5px] text-black/85 hover:bg-black/5 hover:text-[#0474F0] mb-0.5"
                }
                onClick={() => handleSectionClick("about-me")}
              >
                <img src={aboutIcon} alt="" className="w-4"/>
                <span className="text-[12.5px] font-sf">About Me</span>
              </div>

              {/* Projects */}
              <div
                className={
                  panelType === "projects"
                    ? "flex items-center gap-2 px-2 py-1.5 rounded-[5px] bg-black/11 mb-0.5 text-[#0474F0]"
                    : "flex items-center gap-2 px-2 py-1.5 rounded-[5px] text-black/85 hover:bg-black/5 hover:text-[#0474F0] mb-0.5"
                }
                onClick={() => handleSectionClick("All projects")}
              >
                <img src={projectsIcon} alt="" className="w-4"/>
                <span className="text-[12.5px] font-sf">Projects</span>
              </div>

              {/* Certificates */}
              <div
                className={
                  panelType === "certificates"
                    ? "flex items-center gap-2 px-2 py-1.5 rounded-[5px] bg-black/11 mb-0.5 text-[#0474F0]"
                    : "flex items-center gap-2 px-2 py-1.5 rounded-[5px] text-black/85 hover:bg-black/5 hover:text-[#0474F0] mb-0.5"
                }
                onClick={() => handleSectionClick("certificates")}
              >
                <img src={certificatesIcon} alt="" className="w-4"/>
                <span className="text-[12.5px] font-sf">Certificates</span>
              </div>

              {/* Resume */}
              <div
                className={
                  panelType === "photos"
                    ? "flex items-center gap-2 px-2 py-1.5 rounded-[5px] bg-black/11 mb-0.5 text-[#0474F0]"
                    : "flex items-center gap-2 px-2 py-1.5 rounded-[5px] text-black/85 hover:bg-black/5 hover:text-[#0474F0] mb-0.5"
                }
                onClick={() => handleSectionClick("photos")}
              >
                <img src={photosIcon} alt="" className="w-4"/>
                <span className="text-[12.5px] font-sf">Photos</span>
              </div>

              {/* Trash */}
              <div className="flex items-center gap-2 px-2 py-1.5 hover:bg-black/5 rounded-[5px] transition-colors cursor-default">
                <BsTrash3 className="text-sm text-[#0474F0]" />
                <span className="text-[12.5px] text-black/85 font-sf">Trash</span>
              </div>
            </div>

            {/* Section 2 */}
            <div className="px-3 mt-4">
              <div className="text-[11px] font-semibold text-black/27 uppercase tracking-[-0.2px] mb-1 px-2">
                Projects
              </div>

              {/* Project 01 */}
              <div className="flex items-center gap-2 px-2 py-1.5 text-black/50 hover:bg-black/5 hover:text-[#0474F0] rounded-[5px] transition-colors cursor-default">
                <FaProjectDiagram className="text-sm" />
                <span className="text-[12.5px] font-sf">Project 01</span>
              </div>

              {/* Project 02 */}
              <div className="flex items-center gap-2 px-2 py-1.5 text-black/50 hover:bg-black/5 hover:text-[#0474F0] rounded-[5px] transition-colors cursor-default">
                <FaProjectDiagram className="text-sm" />
                <span className="text-[12.5px] font-sf">Project 02</span>
              </div>

              {/* Project 03 */}
              <div className="flex items-center gap-2 px-2 py-1.5 text-black/50 hover:bg-black/5 hover:text-[#0474F0] rounded-[5px] transition-colors cursor-default">
                <FaProjectDiagram className="text-sm" />
                <span className="text-[12.5px]  font-sf">Project 03</span>
              </div>

              {/* Project 04 */}
              <div className="flex items-center gap-2 px-2 py-1.5 text-black/50 hover:bg-black/5 hover:text-[#0474F0] rounded-[5px] transition-colors cursor-default">
                <FaProjectDiagram className="text-sm" />
                <span className="text-[12.5px]   font-sf">Project 04</span>
              </div>
            </div>
          </div>

          {/* Main Content Area - Right panel */}
          <div className="flex-1 bg-white rounded-tr-[10px] rounded-br-[10px] overflow-hidden">
            {/* Toolbar */}
            <div
              className="h-[52px] px-4 flex items-center"
              style={{
                backdropFilter: "blur(13.5914px)",
                WebkitBackdropFilter: "blur(13.5914px)",
                backgroundColor: "rgba(252, 252, 252, 0.8)",
                boxShadow: "0 1px rgba(0,0,0,0.05), 0 0.5px rgba(0,0,0,0.1)",
                borderBottom: "1px solid rgba(0,0,0,0.05)",
              }}

            >
              {
                (isSubPanelOpen || isImagePanel) &&
                <div className="bg-slate-200 h-6 w-6 mr-2 flex items-center rounded-full"
                  onClick={() => {
                    setIsSubPanelOpen(false)
                    setIsImagePanel(false)
                    setTitle(panelType)
                  }
                  }>
                  <IoChevronBack className="h-4 w-7" />
                </div>
              }
              <div>
                <p className="text-[13px] font-bold text-black/70 font-sf capitalize">{title}</p>
                 {
                  !isSubPanelOpen ?
                  <p className="text-[11px] text-black/50 font-sf">Recent {title === "All projects" ? "projects" : title }</p>
                  :""
                }
              </div>
            </div>

            {/* Content */}
            <div className="h-[calc(100%-52px)] overflow-auto no-scrollbar">
              {children || 
              <Panel 
              panelType={panelType} 
              imageClick={imageClick} 
              docClick={docClick} 
              setTitle={setTitle} 
              setIsSubPanelOpen={setIsSubPanelOpen} 
              isSubPanelOpen={isSubPanelOpen}
              isImagePanel = {isImagePanel}
              setIsImagePanel={setIsImagePanel}
              setSelectedProject={setSelectedProject}
              selectedProject={selectedProject}
              >
                </Panel>}
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
}